import React from 'react'

import agent from 'agent'
import { EndpointResponse } from 'types/api/end-point'
import {
  EvaluationConfiguration,
  EvaluationResults
} from 'types/api/services/evaluation'
import {
  GoldenSignalEvaluationConfiguration,
  ResponseBodyEvaluation,
  ServiceGoldenSignal,
  StatusCheckHeader,
  TeamExternalIntegration
} from 'types/api/services/golden-signal'
import {
  convertToK6Url,
  k6LoadGeneratorIntegration
} from 'utils/load-generators/load-generator-integrations-util'
import { asyncRetryWithBackoff } from 'utils/retry'
import {
  appdynamicsResponseBodyEvaluationConfiguration,
  appdynamicsStatusCheckIntegration,
  convertToAppDynamicsBaseUrl,
  convertToAppDynamicsUrl
} from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  convertToDatadogApiUrl,
  datadogResponseBodyEvaluationConfiguration,
  dataDogStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog'
import {
  convertToDatadogEUApiUrl,
  datadogEUResponseBodyEvaluationConfiguration,
  dataDogEUStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import {
  convertToDynatraceBaseUrl,
  convertToDynatraceUrl,
  dynatraceResponseBodyEvaluationConfiguration,
  dynatraceStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  convertToGrafanaBaseUrl,
  convertToGrafanaUrl,
  formatResponseBodyEvaluationPredicatesGrafana,
  grafanaResponseBodyEvaluationConfiguration,
  grafanaStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-grafana'
import {
  computeNewRelicResponseBodyEvaluationConfiguration,
  convertToNewRelicUrl,
  newRelicStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-newrelic'
import {
  convertToPagerDutyUrl,
  pagerDutyResponseBodyEvaluationConfiguration,
  pagerDutyStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-pagerduty'
import { prometheusStatusCheckIntegration } from 'utils/status-checks/status-check-integrations-util-prometheus'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { ENDPOINT_URLS } from 'utils/third-party'

export const customStatusCheckIntegration: TeamExternalIntegration = {
  name: 'Other',
  type: IntegrationType.Custom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 8
}

export const customLoadGeneratorIntegration: TeamExternalIntegration = {
  name: 'Other',
  type: IntegrationType.LoadGeneratorCustom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 9,
  isConfigured: false,
  isOther: true
}

export const teamExternalIntegrationsObject = {
  pagerduty: pagerDutyStatusCheckIntegration,
  newrelic: newRelicStatusCheckIntegration,
  datadog: dataDogStatusCheckIntegration,
  datadog_eu: dataDogEUStatusCheckIntegration,
  grafana: grafanaStatusCheckIntegration,
  dynatrace: dynatraceStatusCheckIntegration,
  appdynamics: appdynamicsStatusCheckIntegration,
  k6: k6LoadGeneratorIntegration
}

export const defaultStatusCheckEvaluationConfiguration: GoldenSignalEvaluationConfiguration =
  {
    healthyStatusCode: '200',
    requestTimeout: 1000,
    responseBodyEvaluation: {
      predicates: [],
      op: 'AND'
    }
  }

export const convertStatusCheckToGoldenSignal = (
  data: any
): ServiceGoldenSignal => {
  if (!data) {
    return null
  }

  return {
    id: data.identifier === 'duplicate' ? '' : data.identifier,
    isContinuous: data.isContinuous,
    integration: {
      type:
        data.teamExternalIntegration?.type?.toLowerCase() ??
        IntegrationType.Custom,
      name: data.teamExternalIntegration?.name || 'Other',
      isPrivateNetworkIntegration: data.isPrivateNetwork,
      canBeUsedAsStatusCheck: data.canBeUsedAsStatusCheck,
      isConfigured: Boolean(data.teamExternalIntegration?.name),
      url: data.endpointConfiguration?.url,
      headers: Object.keys(data.endpointConfiguration?.headers).map((k) => ({
        key: k,
        value: data.endpointConfiguration.headers[k],
        label: 'Value'
      }))
    } as TeamExternalIntegration,
    name: data.name,
    description: data.description,
    evaluationConfiguration: {
      healthyStatusCode: data.evaluationConfiguration?.okStatusCodes[0],
      requestTimeout: data.evaluationConfiguration?.okLatencyMaxMs,
      ...(data.evaluationConfiguration.responseBodyEvaluation
        ? {
            responseBodyEvaluation: {
              op: data.evaluationConfiguration.responseBodyEvaluation.op,
              predicates:
                data.evaluationConfiguration.responseBodyEvaluation
                  .predicates || []
            }
          }
        : { responseBodyEvaluation: { op: 'AND', predicates: [] } })
    }
  }
}

export const convertResponseToTeamExternalIntegration = (
  data: any
): TeamExternalIntegration[] => {
  return (
    data?.integrations?.map((d) => ({
      id: d?.id,
      name: d?.name,
      url:
        d.type?.toLowerCase() === IntegrationType.Custom ||
        d.type?.toLowerCase() === IntegrationType.Grafana ||
        d.type?.toLowerCase() === IntegrationType.Dynatrace ||
        d.type?.toLowerCase() === IntegrationType.Appdynamics ||
        d.type?.toLowerCase() === IntegrationType.LoadGeneratorCustom
          ? d?.url
          : ENDPOINT_URLS[d.type?.toLowerCase()],
      headers: Object.keys(d?.headers)
        .sort()
        .map((k) => ({
          key: k,
          value: d.headers[k],
          label: teamExternalIntegrationsObject[
            d.type?.toLowerCase()
          ]?.headers.find((h) => h.key === k)?.label
        })),
      isConfigured: true,
      type: d.type?.toLowerCase(),
      isPrivateNetworkIntegration: d.privateNetwork,
      lastAuthenticationStatus: d.lastAuthenticationStatus,
      canBeUsedAsStatusCheck: d.canBeUsedAsStatusCheck,
      sortIndex:
        teamExternalIntegrationsObject[d.type?.toLowerCase()]?.sortIndex ?? 5
    })) ?? []
  ).sort((a, b) => a.sortIndex - b.sortIndex)
}

export const removeSavedTeamExternalIntegrations = (
  integrations: TeamExternalIntegration[],
  savedIntegrations: TeamExternalIntegration[]
): TeamExternalIntegration[] => {
  const savedIntegrationsNames = savedIntegrations.map((i) => i.name)
  return integrations.filter((i) => !savedIntegrationsNames.includes(i.name))
}

export const completeTeamExternalIntegrations = (
  externalIntegrationType: 'GOLDEN_SIGNAL' | 'LOAD_GENERATOR',
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  switch (externalIntegrationType) {
    case 'GOLDEN_SIGNAL':
      return completeGoldenSignalIntegrations(integrations, includeCustom)
    case 'LOAD_GENERATOR':
      return completeLoadGeneratorIntegrations(integrations, includeCustom)
  }
}

export const completeGoldenSignalIntegrations = (
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const completedIntegrations = [...integrations]
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Datadog
    )
  ) {
    completedIntegrations.push(dataDogStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.DatadogEU
    )
  ) {
    completedIntegrations.push(dataDogEUStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Pagerduty
    )
  ) {
    completedIntegrations.push(pagerDutyStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Newrelic
    )
  ) {
    completedIntegrations.push(newRelicStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Prometheus
    )
  ) {
    completedIntegrations.push(prometheusStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Grafana
    )
  ) {
    completedIntegrations.push(grafanaStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Dynatrace
    )
  ) {
    completedIntegrations.push(dynatraceStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Appdynamics
    )
  ) {
    completedIntegrations.push(appdynamicsStatusCheckIntegration)
  }
  if (includeCustom) {
    completedIntegrations.push(customStatusCheckIntegration)
  }

  return completedIntegrations.sort((a, b) => a.sortIndex - b.sortIndex)
}

export const completeLoadGeneratorIntegrations = (
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const completedIntegrations = [...integrations]
  if (
    !integrations.find((i) => i?.type?.toLowerCase() === IntegrationType.K6)
  ) {
    completedIntegrations.push(k6LoadGeneratorIntegration)
  }
  if (includeCustom) {
    completedIntegrations.push(customLoadGeneratorIntegration)
  }
  return completedIntegrations.sort((a, b) => a.sortIndex - b.sortIndex)
}

export function computeResponseBodyEvaluation(
  integration: TeamExternalIntegration
): ResponseBodyEvaluation {
  switch (integration?.type ?? IntegrationType.Custom) {
    case IntegrationType.Datadog:
      return datadogResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogEU:
      return datadogEUResponseBodyEvaluationConfiguration
    case IntegrationType.Pagerduty:
      return pagerDutyResponseBodyEvaluationConfiguration
    case IntegrationType.Newrelic:
      return computeNewRelicResponseBodyEvaluationConfiguration(integration.url)
    case IntegrationType.Grafana:
      return grafanaResponseBodyEvaluationConfiguration
    case IntegrationType.Dynatrace:
      return dynatraceResponseBodyEvaluationConfiguration
    case IntegrationType.Appdynamics:
      return appdynamicsResponseBodyEvaluationConfiguration
  }
}

export async function convertToApiUrl(
  integration: TeamExternalIntegration,
  teamId: string
): Promise<string> {
  if (!integration?.url) {
    return ''
  }

  switch (integration.type) {
    case IntegrationType.Datadog:
      return convertToDatadogApiUrl(integration.url)
    case IntegrationType.DatadogEU:
      return convertToDatadogEUApiUrl(integration.url)
    case IntegrationType.Pagerduty:
      return await convertToPagerDutyUrl(integration, teamId)
    case IntegrationType.Newrelic:
      return convertToNewRelicUrl(integration.url)?.trim()
    case IntegrationType.Grafana:
      return convertToGrafanaUrl(integration.url)
    case IntegrationType.Dynatrace:
      return convertToDynatraceUrl(integration.url)
    case IntegrationType.Appdynamics:
      return convertToAppDynamicsUrl(integration.url)
    case IntegrationType.K6:
      return convertToK6Url(integration.url)?.trim()
    default:
      return integration.url?.trim()
  }
}

export function convertToBaseUrl(integration) {
  if (!integration?.url) {
    return ''
  }

  switch (integration.type) {
    case IntegrationType.Grafana:
      return convertToGrafanaBaseUrl(integration.url)
    case IntegrationType.Dynatrace:
      return convertToDynatraceBaseUrl(integration.url)
    case IntegrationType.Appdynamics:
      return convertToAppDynamicsBaseUrl(integration.url)
    default:
      return integration.url?.trim()
  }
}

export const formatResponseBodyEvaluationPredicates = (
  responseBodyEvaluation: ResponseBodyEvaluation
) => {
  return {
    op: responseBodyEvaluation.op,
    predicates:
      responseBodyEvaluation?.predicates?.map((p) => ({
        ...p,
        jpQuery: p.jpQuery.startsWith('.') ? p.jpQuery : `.${p.jpQuery}`
      })) ?? []
  }
}

export const addAuthorizationPrefixIfNeeded = (
  type: IntegrationType,
  authHeader: StatusCheckHeader
) => {
  const OBFUSCATION_PREFIX = '****'
  if (
    authHeader.key === 'Authorization' &&
    !authHeader.value.trim().startsWith(OBFUSCATION_PREFIX)
  ) {
    switch (type) {
      case IntegrationType.Pagerduty:
        return authHeader.value.trim().startsWith('Token token=')
          ? authHeader.value
          : `Token token=${authHeader.value.trim()}`
      case IntegrationType.Grafana:
      case IntegrationType.Appdynamics:
        return authHeader.value.trim().startsWith('Bearer ')
          ? authHeader.value
          : `Bearer ${authHeader.value.trim()}`
      case IntegrationType.Dynatrace:
        return authHeader.value.trim().startsWith('Api-Token ')
          ? authHeader.value
          : `Api-Token ${authHeader.value.trim()}`
      case IntegrationType.K6:
        return authHeader.value.trim().startsWith('token ')
          ? authHeader.value
          : `token ${authHeader.value.trim()}`
      default:
        return authHeader.value
    }
  }
  return authHeader.value
}

export const prepareHeaders = (integration: TeamExternalIntegration) => {
  const headers = integration.headers.reduce((result, item) => {
    result[item.key] = addAuthorizationPrefixIfNeeded(integration.type, item)
    return result
  }, {})

  if (integration.type === IntegrationType.Dynatrace) {
    if (!('Content-Type' in headers)) {
      headers['Content-Type'] = 'application/json'
    }
  }
  return headers
}

export const testStatusCheckEvaluation = async (
  evaluationConfiguration: GoldenSignalEvaluationConfiguration,
  endpointResponse: EndpointResponse,
  teamId: string,
  integration: TeamExternalIntegration,
  onSuccess: (evaluationResults: EvaluationResults) => void,
  onError: (err: Error) => void
) => {
  const request = {
    evaluationConfiguration: {
      okLatencyMaxMs: evaluationConfiguration.requestTimeout,
      okStatusCodes: [evaluationConfiguration.healthyStatusCode],
      responseBodyEvaluation:
        integration == null || integration.type != IntegrationType.Grafana
          ? formatResponseBodyEvaluationPredicates(
              evaluationConfiguration.responseBodyEvaluation
            )
          : formatResponseBodyEvaluationPredicatesGrafana(integration.url)
    } as EvaluationConfiguration,
    testEndpointResponse: {
      ...endpointResponse,
      contentType: endpointResponse.contentType || 'text/plain'
    } as EndpointResponse
  }

  try {
    const { data } = await agent.post(
      `status-checks/runTestEvaluation?teamId=${teamId}`,
      request
    )

    if (data?.testEvaluationResults) {
      onSuccess(data.testEvaluationResults)
    } else {
      onError(new Error('No evaluation results received'))
    }
  } catch (err) {
    onError(err)
  }
}

export const testTeamExternalIntegrationEndpointConfiguration = async (
  integration: TeamExternalIntegration,
  teamId: string,
  abortControllerRef: React.MutableRefObject<AbortController>,
  onSuccess: (response: EndpointResponse) => void,
  onError: (responseBody: string) => void,
  statusCheckId?: string,
  authenticate?: boolean
) => {
  const integrationUrl =
    (await convertToApiUrl(integration, teamId)) ||
    teamExternalIntegrationsObject?.[integration.type.toLowerCase()]?.authUrl
  try {
    new URL(integrationUrl)
  } catch (e) {
    onError(`integration url (${integrationUrl}) is not a valid url`)
    return
  }

  const requestData = {
    endpointConfiguration: {
      url: encodeQueryParams(integrationUrl),
      ...((authenticate ||
        (!statusCheckId && !integration.isConfigured) ||
        integration.type === IntegrationType.Custom) && {
        headers: prepareHeaders(integration)
      })
    },
    ...(integration.isConfigured && {
      teamExternalIntegration: {
        type: integration.type?.toUpperCase(),
        name: integration.name
      }
    }),
    ...(!integration.isConfigured &&
      statusCheckId && {
        webhookId: statusCheckId
      })
  }

  try {
    if (integration.isPrivateNetworkIntegration) {
      abortControllerRef.current = new AbortController()
      const { data } = await agent.post(
        `integration-invocations?teamId=${teamId}`,
        requestData,
        {
          signal: abortControllerRef.current.signal
        }
      )

      const invocationId = data?.invocationId
      await asyncRetryWithBackoff(
        500,
        20 * 1000,
        async () => {
          let invocationResponse
          try {
            invocationResponse = await agent.post(
              `integration-invocations/${invocationId}/run-test-endpoint?teamId=${teamId}`,
              null,
              { signal: abortControllerRef.current.signal }
            )
          } catch (err) {
            if (err.name === 'CanceledError') {
              onError('Canceled')
            } else {
              onError(null)
            }
            return true
          }
          return handlePrivateNetworkInvocationResponse(
            invocationResponse,
            onSuccess,
            onError
          )
        },
        () => {},
        () => {
          onError(null)
        }
      )
    } else {
      abortControllerRef.current = new AbortController()
      const { data } = await agent.post(
        `status-checks/run-test-endpoint?teamId=${teamId}`,
        requestData,
        {
          signal: abortControllerRef.current.signal
        }
      )

      if (
        isValidStatusCodeOrRange(
          data?.testEndpointResponse?.httpStatusCode?.toString()
        )
      ) {
        onSuccess(data?.testEndpointResponse)
      } else {
        onError(data?.testEndpointResponse?.responseBody)
      }
    }
  } catch (err) {
    if (err.name === 'CanceledError') {
      onError('Canceled')
    } else {
      onError(null)
    }
  }
}

/**
 * Handles the private network invocation response
 *
 * @param invocationResponse
 * @param onSuccess
 * @param onError
 * @return {boolean} <b>true</b> if the invocation has finished processing, <b>false</b> otherwise
 */
const handlePrivateNetworkInvocationResponse = (
  invocationResponse,
  onSuccess,
  onError: (responseBody: string) => void
) => {
  const { errorMessage, testEndpointResponse } = invocationResponse.data

  if (invocationResponse.status === 204) {
    //status is No Content - not ready yet
    return false
  } else if (errorMessage) {
    onError(null)
    return true
  }

  if (
    isValidStatusCodeOrRange(testEndpointResponse?.httpStatusCode?.toString())
  ) {
    onSuccess(testEndpointResponse)
  } else {
    onError(testEndpointResponse.responseBody)
  }

  return true
}

export const isEncoded = (uri) => uri !== decodeURIComponent(uri || '')

export const encodeQueryParams = (url) => {
  if (!isEncoded(url)) {
    let Url
    try {
      Url = new URL(url)
    } catch (e) {
      // If we get an invalid URL just give up
      return url
    }

    const fragment = Url.hash
    const [requestUrl, queryParamsString] = url
      .replace(fragment, '')
      .split(/\?(.*)/s)

    if (queryParamsString) {
      const encodedParams = []
      queryParamsString.split('&').forEach((queryParam) => {
        const [param, value] = queryParam.split('=')
        encodedParams.push(
          value ? `${param}=${encodeURIComponent(value)}` : param
        )
      })
      return fragment
        ? `${requestUrl}?${encodedParams.join('&')}${fragment}`
        : `${requestUrl}?${encodedParams.join('&')}`
    }
  }

  return url
}

export const isValidStatusCodeOrRange = (v) => /^2\d{2}(-2\d{2})?$/.test(v) // 2xx or 2xx-2xx
